import { useState } from 'react';
import { addBulkSuites } from 'src/services/client';
import { toast } from 'react-toastify';
import { transformSuiteDataFromCSV } from 'src/pages/Property/CustomHooks/UseBulkSaveSuite/helpers';
import {
  BulkSuiteImportParams,
  ProcessedSuite,
} from 'src/pages/Property/CustomHooks/UseBulkSaveSuite/BulkSaveSuiteAPITypes';

interface BulkSuiteResponse {
  statusCode: number;
  data: any[];
}

interface UseSaveBulkSuitesReturn {
  saveBulkSuitesResponse: any[] | null;
  suitesError: string | null;
  isLoading: boolean;
  isSuccess: boolean;
  fetchSaveBulkSuitesResponse: (
    suitesArray: any,
    propertyId: any,
    userID: any
  ) => Promise<boolean>;
  clearResponse: () => void;
}

export const useSaveBulkSuites = (): UseSaveBulkSuitesReturn => {
  const [saveBulkSuitesResponse, setSaveBulkSuitesResponse] = useState<
    any[] | null
  >(null);
  const [suitesError, setSuitesError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const fetchSaveBulkSuitesResponseFromAPI = async (
    suitesArray: any[],
    propertyId: string,
    userID: string
  ): Promise<boolean> => {
    try {
      setIsLoading(true);
      setSuitesError(null);
      setIsSuccess(false);

      const transformedSuites: ProcessedSuite[] =
        transformSuiteDataFromCSV(suitesArray);

      const params: BulkSuiteImportParams = {
        propertyId: propertyId,
        userId: userID,
        suiteList: transformedSuites,
      };

      const response: any = await addBulkSuites(params);
      const responseJson: BulkSuiteResponse = await response.json();
      setSaveBulkSuitesResponse(responseJson.data || []);

      if (response.status !== 200) {
        setSuitesError('Failed to save bulk suites');
        toast.error('🚫 Failed to save bulk suites, please try again.', {
          autoClose: 12000,
          position: 'top-center',
        });

        return false;
      }

      setIsSuccess(true);
      toast.success('✅ Suites imported successfully!', {
        autoClose: 8000,
        position: 'top-center',
      });
    } catch (error) {
      console.error('Error saving bulk suites:', error);
      setSuitesError('Error occurred while saving suites');
      toast.error('🚫 Something went wrong, please try again.', {
        autoClose: 12000,
        position: 'top-center',
      });

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const clearResponse = () => {
    setSaveBulkSuitesResponse(null);
    setSuitesError(null);
    setIsSuccess(false);
  };

  return {
    saveBulkSuitesResponse,
    suitesError,
    isLoading,
    isSuccess,
    fetchSaveBulkSuitesResponse: fetchSaveBulkSuitesResponseFromAPI,
    clearResponse,
  };
};
